import * as angular from "angular";
import type { JsonSchemaDefinition } from "../../../../extensions/json-schema/runtime";
import { isRecord } from "../../../../extensions/json-schema/runtime";
import type { ModalTabItem } from "../../../modals/tabbed-modal/tabbed-modal-types";
import { globs } from "../../../../services/globs";
import {
	buildEntitySchemaEntries,
	EntitySchemaEntry,
	mergeEntitySchemaSection
} from "../../entity-schema-state";
import viewModesSection from "../../../view-modes-section";
import entitySchemaViewer from "./components/entity-schema-tab/entity-schema-viewer/entity-schema-viewer";
import entitySchemaEditor from "./components/entity-schema-tab/entity-schema-editor/entity-schema-editor";
import template from "./entity-schema-tabs.html";
import "./entity-schema-tabs.less";

interface EntitySchemaModalTab extends EntitySchemaEntry {
	code: string;

	tab: ModalTabItem;
}

export interface EntitySchemaTabsController {
	entity: Record<string, unknown>;

	typeConfig: {
		Code: string;
		Schema?: JsonSchemaDefinition;
	};

	save: (args: { entity: Record<string, unknown> }) => unknown | PromiseLike<unknown>;
}

// AngularJS owns the merged binding properties.
// eslint-disable-next-line no-redeclare
export class EntitySchemaTabsController implements angular.IController {
	public static $inject = ["securityService"];

	public entries: EntitySchemaModalTab[] = [];

	public isReadonly = true;

	public constructor(private readonly securityService: {
		isActionAllowed(permission: string): boolean;
	}) {}

	public $onInit(): void {
		const schema = this.typeConfig.Schema;

		this.entries = schema ? buildEntitySchemaEntries(schema, this.entity)
			.map(entry => {
				const code = `SCHEMA_${entry.key.toUpperCase()}`;

				return {
					...entry,
					code,
					tab: {
						name: entry.title,
						code
					}
				};
			}) : [];
		this.isReadonly = !this.securityService.isActionAllowed(globs.entityTypes.byType(this.typeConfig.Code).write);
		this.refreshModel();
	}

	public $onChanges(changes: angular.IOnChangesObject): void {
		if(changes.entity && !changes.entity.isFirstChange()) {
			this.refreshModel(changes.entity.currentValue);
		}
	}

	public async saveModel(entry: EntitySchemaModalTab, model: unknown): Promise<unknown> {
		if(this.isReadonly) {
			return false;
		}

		const candidate = mergeEntitySchemaSection(this.entity, entry.key, model);
		const result = await this.save({ entity: candidate });
		this.refreshModel(isRecord(result) ? {
			...candidate,
			...result
		} : candidate);

		return result;
	}

	private refreshModel(entity = this.entity): void {
		for(const entry of this.entries) {
			entry.value = entity[entry.key];
		}
	}
}

export default angular.module("vesl.components.entity-modal.tabs.entity-schema-tabs", [
	viewModesSection,
	entitySchemaViewer,
	entitySchemaEditor
])
	.component("entitySchemaTabs", {
		controller: EntitySchemaTabsController,
		template,
		bindings: {
			entity: "<",
			typeConfig: "<",
			save: "&"
		}
	})
	.name;
